// Dates et fuseaux, sans dépendance : Intl suffit, partout (Convex, navigateur).
//
// Deux formes de date coexistent dans les champs :
// - un JOUR, "YYYY-MM-DD", sans fuseau : il désigne une case du calendrier,
//   pas un instant. Le stocker comme un instant UTC le décale d'un jour selon
//   le fuseau du lecteur.
// - un INSTANT, ISO 8601 UTC ("2026-10-23T12:00:00.000Z"), affiché dans un
//   fuseau.

const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86_400_000;

export function isIsoDay(value: string): boolean {
  const match = DAY_RE.exec(value);
  if (!match) return false;
  const [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(Date.UTC(y, m - 1, d));
  return (
    date.getUTCFullYear() === y &&
    date.getUTCMonth() === m - 1 &&
    date.getUTCDate() === d
  );
}

export function isIsoInstant(value: string): boolean {
  return (
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})$/.test(
      value,
    ) && !Number.isNaN(Date.parse(value))
  );
}

/** Le jour (`YYYY-MM-DD`) d'une date stockée, avec ou sans heure. */
export function dayOf(value: string): string {
  return value.slice(0, 10);
}

export function dayFromParts(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Jour → minuit UTC de ce jour, pour l'arithmétique et les comparaisons. */
export function dayToUtcMs(day: string): number {
  const [y, m, d] = day.split("-").map(Number);
  return Date.UTC(y!, m! - 1, d!);
}

export function addDays(day: string, days: number): string {
  const date = new Date(dayToUtcMs(day) + days * DAY_MS);
  return dayFromParts(
    date.getUTCFullYear(),
    date.getUTCMonth() + 1,
    date.getUTCDate(),
  );
}

export function addMonths(day: string, months: number): string {
  const [y, m, d] = day.split("-").map(Number);
  const target = new Date(Date.UTC(y!, m! - 1 + months, 1));
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  return dayFromParts(
    target.getUTCFullYear(),
    target.getUTCMonth() + 1,
    Math.min(d!, lastDay),
  );
}

/** 0 = dimanche … 6 = samedi. */
export function weekdayOfDay(day: string): number {
  return new Date(dayToUtcMs(day)).getUTCDay();
}

export function daysBetween(from: string, to: string): number {
  return Math.round((dayToUtcMs(to) - dayToUtcMs(from)) / DAY_MS);
}

type ZonedParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

const partsFormatters = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = partsFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    partsFormatters.set(timeZone, formatter);
  }
  return formatter;
}

export function isValidTimeZone(timeZone: string): boolean {
  try {
    partsFormatter(timeZone);
    return true;
  } catch {
    return false;
  }
}

export function zonedParts(epochMs: number, timeZone: string): ZonedParts {
  const parts: Record<string, number> = {};
  for (const part of partsFormatter(timeZone).formatToParts(epochMs)) {
    if (part.type !== "literal") parts[part.type] = Number(part.value);
  }
  return {
    year: parts.year!,
    month: parts.month!,
    day: parts.day!,
    hour: parts.hour! % 24,
    minute: parts.minute!,
    second: parts.second!,
  };
}

/** Le jour qu'il est, à cet instant, dans ce fuseau. */
export function dayInZone(epochMs: number, timeZone: string): string {
  const p = zonedParts(epochMs, timeZone);
  return dayFromParts(p.year, p.month, p.day);
}

function offsetMs(epochMs: number, timeZone: string): number {
  const p = zonedParts(epochMs, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - Math.floor(epochMs / 1000) * 1000;
}

/**
 * L'instant d'une heure murale dans un fuseau (« le 23 à 14 h, à Paris »).
 * Deux passes pour les changements d'heure ; une heure qui n'existe pas
 * (saut de printemps) glisse sur la suivante.
 */
export function zonedToEpoch(
  day: string,
  hour: number,
  minute: number,
  timeZone: string,
): number {
  const [y, m, d] = day.split("-").map(Number);
  const wall = Date.UTC(y!, m! - 1, d!, hour, minute);
  let epoch = wall - offsetMs(wall, timeZone);
  const corrected = wall - offsetMs(epoch, timeZone);
  if (corrected !== epoch) epoch = corrected;
  return epoch;
}

// ── Lecture de dates en langage naturel ──────────────────────────────────

const MONTHS: Record<string, number> = {
  janvier: 1, janv: 1, jan: 1, january: 1,
  fevrier: 2, fevr: 2, fev: 2, feb: 2, february: 2,
  mars: 3, mar: 3, march: 3,
  avril: 4, avr: 4, apr: 4, april: 4,
  mai: 5, may: 5,
  juin: 6, jun: 6, june: 6,
  juillet: 7, juil: 7, jul: 7, july: 7,
  aout: 8, aug: 8, august: 8,
  septembre: 9, sept: 9, sep: 9, september: 9,
  octobre: 10, oct: 10, october: 10,
  novembre: 11, nov: 11, november: 11,
  decembre: 12, dec: 12, december: 12,
};

const WEEKDAYS: Record<string, number> = {
  dimanche: 0, sunday: 0, sun: 0, dim: 0,
  lundi: 1, monday: 1, mon: 1, lun: 1,
  mardi: 2, tuesday: 2, tue: 2,
  mercredi: 3, wednesday: 3, wed: 3, mer: 3,
  jeudi: 4, thursday: 4, thu: 4, jeu: 4,
  vendredi: 5, friday: 5, fri: 5, ven: 5,
  samedi: 6, saturday: 6, sat: 6, sam: 6,
};

const RELATIVE_DAYS: Record<string, number> = {
  "aujourd'hui": 0, aujourdhui: 0, today: 0, auj: 0,
  demain: 1, tomorrow: 1,
  hier: -1, yesterday: -1,
  "apres-demain": 2, "apres demain": 2,
  "avant-hier": -2, "avant hier": -2,
};

const UNITS: Record<string, "day" | "week" | "month" | "year"> = {
  jour: "day", jours: "day", j: "day", day: "day", days: "day", d: "day",
  semaine: "week", semaines: "week", sem: "week", week: "week", weeks: "week", w: "week",
  mois: "month", month: "month", months: "month",
  an: "year", ans: "year", annee: "year", annees: "year", year: "year", years: "year", y: "year",
};

function shiftDay(day: string, amount: number, unit: "day" | "week" | "month" | "year"): string {
  switch (unit) {
    case "day":
      return addDays(day, amount);
    case "week":
      return addDays(day, amount * 7);
    case "month":
      return addMonths(day, amount);
    case "year":
      return addMonths(day, amount * 12);
  }
}

export type ParsedDateTime = { day: string; time?: { hour: number; minute: number } };

function normalize(text: string): string {
  return text
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[’`]/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

/** Extrait une heure en fin de texte : « 14h », « 14h30 », « 14:30 », « 2pm ». */
function splitTime(text: string): { rest: string; time?: { hour: number; minute: number } } | null {
  const match =
    /(?:^|\s)(?:a |at |@ ?)?(\d{1,2})(?:(?:h|:)(\d{2})?|\s?(am|pm))(?:\s?(am|pm))?$/.exec(text);
  if (!match) return { rest: text };
  let hour = Number(match[1]);
  const minute = match[2] ? Number(match[2]) : 0;
  const meridiem = match[3] ?? match[4];
  if (meridiem === "pm" && hour < 12) hour += 12;
  if (meridiem === "am" && hour === 12) hour = 0;
  if (hour > 23 || minute > 59) return null;
  return { rest: text.slice(0, match.index).trim(), time: { hour, minute } };
}

function parseDayPart(text: string, today: string, locale: string): string | null {
  if (text === "") return today;
  if (isIsoDay(text)) return text;

  if (text in RELATIVE_DAYS) return addDays(today, RELATIVE_DAYS[text]!);

  // « dans 3 jours », « in 2 weeks », « +3d », « il y a 2 jours », « 3 days ago »
  const ahead = /^(?:dans|in|\+)\s?(\d+)\s?([a-z]+)$/.exec(text);
  if (ahead && UNITS[ahead[2]!]) return shiftDay(today, Number(ahead[1]), UNITS[ahead[2]!]!);
  const ago =
    /^(?:il y a\s?(\d+)\s?([a-z]+)|(\d+)\s?([a-z]+) ago|-\s?(\d+)\s?([a-z]+))$/.exec(text);
  if (ago) {
    const amount = Number(ago[1] ?? ago[3] ?? ago[5]);
    const unit = UNITS[(ago[2] ?? ago[4] ?? ago[6])!];
    if (unit) return shiftDay(today, -amount, unit);
  }

  // « lundi », « lundi prochain », « next monday » : la prochaine occurrence,
  // strictement après aujourd'hui.
  const weekday = /^(?:next |ce |this )?([a-z]+)(?: prochain| next)?$/.exec(text);
  if (weekday && weekday[1]! in WEEKDAYS) {
    const target = WEEKDAYS[weekday[1]!]!;
    const delta = (target - weekdayOfDay(today) + 7) % 7 || 7;
    return addDays(today, delta);
  }

  // 23/10/2026, 23.10.26, 23-10 ; mois d'abord en anglais américain.
  const numeric = /^(\d{1,2})[/.-](\d{1,2})(?:[/.-](\d{2,4}))?$/.exec(text);
  if (numeric) {
    const monthFirst = locale.toLowerCase() === "en-us";
    const a = Number(numeric[1]);
    const b = Number(numeric[2]);
    const [day, month] = monthFirst ? [b, a] : [a, b];
    const year = yearOf(numeric[3], today);
    return validDay(year, month, day);
  }

  // « 23 octobre 2026 », « 23 oct », « october 23, 2026 », « oct 23 »
  const dayMonth = /^(?:[a-z]+\.? )?(\d{1,2})(?:er)? ([a-z]+)\.?(?: (\d{2,4}))?$/.exec(text);
  if (dayMonth && MONTHS[dayMonth[2]!]) {
    return validDay(yearOf(dayMonth[3], today), MONTHS[dayMonth[2]!]!, Number(dayMonth[1]));
  }
  const monthDay = /^([a-z]+)\.? (\d{1,2})(?:st|nd|rd|th)?,?(?: (\d{2,4}))?$/.exec(text);
  if (monthDay && MONTHS[monthDay[1]!]) {
    return validDay(yearOf(monthDay[3], today), MONTHS[monthDay[1]!]!, Number(monthDay[2]));
  }
  return null;
}

function yearOf(raw: string | undefined, today: string): number {
  if (!raw) return Number(today.slice(0, 4));
  const year = Number(raw);
  return raw.length === 2 ? 2000 + year : year;
}

function validDay(year: number, month: number, day: number): string | null {
  const candidate = dayFromParts(year, month, day);
  return isIsoDay(candidate) ? candidate : null;
}

/**
 * Une date écrite par un humain ou un LLM → un jour, et une heure si elle
 * est donnée. « Aujourd'hui » est le jour de `now` dans `timeZone`.
 */
export function parseNaturalDate(
  input: string,
  ctx: { now: number; timeZone: string; locale: string },
): ParsedDateTime | null {
  const text = normalize(input);
  if (text === "") return null;
  const today = dayInZone(ctx.now, ctx.timeZone);

  // « 2026-10-23 14:30 », « 2026-10-23T14:30 » sans fuseau : heure murale.
  const isoWall = /^(\d{4}-\d{2}-\d{2})[t ](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/.exec(text);
  if (isoWall && isIsoDay(isoWall[1]!)) {
    return { day: isoWall[1]!, time: { hour: Number(isoWall[2]), minute: Number(isoWall[3]) } };
  }

  const split = splitTime(text);
  if (!split) return null;
  // « demain » seul, ou « 14h » seul (aujourd'hui à 14 h).
  const day = parseDayPart(split.rest, today, ctx.locale);
  if (!day) return null;
  if (split.rest === "" && !split.time) return null;
  return split.time ? { day, time: split.time } : { day };
}
